import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getAllAssessment, getAllAssessmentByUser } from "../axios/axiosFunctions";
import { UserContext } from "./user";
import { isTimFamilySlug } from "../utils/timFamily";

// Display-time block-name renames applied to fetched assessments.
// The DB is the source of truth; once a backend migration updates the
// stored Block.name, the matching entry can be removed here.
const BLOCK_NAME_RENAMES = {
    "Clear Communication Inside and Out": "Clear Communication Inside and Outside the Team",
};

const renameBlock = (b) => (
    b ? { ...b, name: BLOCK_NAME_RENAMES[b.name] || b.name } : b
);

const applyBlockRenames = (assessments) => {
    if (!Array.isArray(assessments)) return assessments;
    return assessments.map((a) => ({
        ...a,
        blocks: Array.isArray(a.blocks) ? a.blocks.map(renameBlock) : a.blocks,
        ownerBlocks: Array.isArray(a.ownerBlocks) ? a.ownerBlocks.map(renameBlock) : a.ownerBlocks,
    }));
};

export const AssessmentContext = createContext({
    allAssessments:null,
    setAllAssessments:()=>null,
    isLoading:true,
});

export const AssessmentsProvider = ({children})=>{

    const {currentUser} = useContext(UserContext)
    const [allAssessments,setAllAssessments] = useState();
    const [isLoading, setIsLoading] = useState(true);

    const fetchAssessments = useCallback(async () => {
        setIsLoading(true); // Establece el estado de carga al iniciar
        try {
            let data = [];
            if(currentUser){
                data = await getAllAssessmentByUser(currentUser._id);
            }else{
                data = await getAllAssessment();
            }
            const renamed = applyBlockRenames(data);
            setAllAssessments(renamed);
            return renamed; // Devuelve los datos para uso externo
        } catch (error) {
            console.error("Error al cargar los Assessments:", error);
            setAllAssessments([]);
            throw error; // Propaga el error para manejo externo
        } finally {
            setIsLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchAssessments();
    }, [fetchAssessments]);

    const refetchAssessments = useCallback(async () => {
        // Simplemente llamamos a la función central de obtención de datos
        // El estado de carga y la actualización ya se manejan dentro de fetchAssessments
        return fetchAssessments();
    }, [fetchAssessments]);

    const getAssessmentBySlug = useCallback((slug) => {
        if (!allAssessments) return null;
        return allAssessments.find(assessment => assessment.slug === slug);
    }, [allAssessments]);

    const getPositionBlockBySlug = useCallback((slugAssessment,slugBlock,rol=undefined)=>{
        if (!allAssessments) return {
            before: null,
            after:null
        };
        const assessment = allAssessments.find(assessment => assessment.slug === slugAssessment);
        let blocks = [...assessment.blocks];
        if(slugAssessment === "mim_360_assessment" || slugAssessment === "lim_360_assessment"){ 
            blocks = [...assessment.ownerBlocks];
        }
        if(rol === "member" && isTimFamilySlug(slugAssessment)){
            blocks.pop();
        }
        if(rol === "senior" && isTimFamilySlug(slugAssessment)){
            blocks = [blocks[blocks.length - 1]];
        }
        // "both" (senior + member): no slicing — walk every block in order, so the
        // member blocks come first and the senior block lands at the end.
        if(!assessment) return null;
        const pos = blocks.findIndex(block => block.slug === slugBlock);
       if (pos === -1) {
            return { before: null, after: null, pos: -1, total: blocks.length };
        }

        const lastIndex = blocks.length - 1;

        return {
            before: pos > 0 ? blocks[pos - 1].slug : null,
            after: pos < lastIndex ? blocks[pos + 1].slug : null,
            // pos is the 0-based index of the current section; total is the
            // number of sections this respondent has to complete (role-filtered).
            pos,
            total: blocks.length,
        };
    },[allAssessments]);

    const getBlockBySlug = useCallback((slugAssessment,slugBlock) =>{
        if (!allAssessments) return null;
        const assessment = allAssessments.find(assessment => assessment.slug === slugAssessment);
        if(!assessment) return null;
        if(slugAssessment === "mim_360_assessment" || slugAssessment === "lim_360_assessment"){
            return assessment.ownerBlocks.find(block => block.slug === slugBlock);
        }
        return assessment.blocks.find(block => block.slug === slugBlock);
    }, [allAssessments]);
    
    const value = {allAssessments,setAllAssessments,isLoading,getAssessmentBySlug,getBlockBySlug,getPositionBlockBySlug,refetchAssessments};

    return <AssessmentContext.Provider value={value}>{children}</AssessmentContext.Provider>
}